import { EASE_IN, EASE_OUT, prefersReducedMotion } from './motion';

type MotionKind = 'scrim' | 'sheet' | 'panel' | 'popover';

interface MotionSpec { from: Keyframe; to: Keyframe; inMs: number; outMs: number }

export const MOTION: Record<MotionKind, MotionSpec> = {
  scrim: { from: { opacity: 0 }, to: { opacity: 1 }, inMs: 240, outMs: 160 },
  sheet: { from: { transform: 'translateY(100%)' }, to: { transform: 'none' }, inMs: 240, outMs: 160 },
  panel: { from: { transform: 'translateX(100%)' }, to: { transform: 'none' }, inMs: 240, outMs: 160 },
  popover: { from: { opacity: 0, transform: 'translateY(-4px) scale(0.96)' }, to: { opacity: 1, transform: 'none' }, inMs: 160, outMs: 120 },
};

const isMotionKind = (value: string | undefined): value is MotionKind => value !== undefined && Object.hasOwn(MOTION, value);

// The same markup is a desktop popover/slide-over or a phone sheet depending on CSS, so pick by rendered box:
// skip display none/contents, and skip anything inside a target that already moves.
export function motionTargets(root: HTMLElement): HTMLElement[] {
  const targets: HTMLElement[] = [];
  for (const el of root.querySelectorAll<HTMLElement>('[data-motion]')) {
    const { display } = getComputedStyle(el);
    if (display === 'none' || display === 'contents') continue;
    if (targets.some((parent) => parent.contains(el))) continue;
    targets.push(el);
  }
  return targets;
}

export type MotionSnapshot = Map<HTMLElement, Keyframe>;

// Where each target visually is right now, so an interrupting animation can continue from there instead of snapping.
export function snapshotMotion(root: HTMLElement): MotionSnapshot {
  const snapshot: MotionSnapshot = new Map();
  for (const el of motionTargets(root)) {
    const kind = el.dataset.motion;
    if (!isMotionKind(kind)) continue;
    const style = getComputedStyle(el);
    const frame: Keyframe = {};
    for (const property of Object.keys(MOTION[kind].from)) frame[property] = style.getPropertyValue(property);
    snapshot.set(el, frame);
  }
  return snapshot;
}

export function playMotion(root: HTMLElement, direction: 'in' | 'out', start?: MotionSnapshot): Animation[] {
  if (prefersReducedMotion()) return [];
  return motionTargets(root).flatMap((el) => {
    const kind = el.dataset.motion;
    if (!isMotionKind(kind)) return [];
    const { from, to, inMs, outMs } = MOTION[kind];
    const here = start?.get(el);
    return direction === 'in'
      ? [el.animate([{ ...from, ...here }, to], { duration: inMs, easing: EASE_OUT })]
      : [el.animate([{ ...to, ...here }, from], { duration: outMs, easing: EASE_IN, fill: 'forwards' })];
  });
}
